import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  ArrowLeft,
  Download,
  Eye,
  MagnifyingGlass,
  FileText,
  Image as ImageIcon,
  EnvelopeSimple,
  Article,
  Book,
  Package,
  TShirt,
  AppWindow,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { API_URL } from '../config/api';
import { SEO } from '../components/SEO';

const auth = () => ({ withCredentials: true });

const SECTION_ICONS = {
  graphics: ImageIcon,
  merch: TShirt,
  pwa: AppWindow,
  emails: EnvelopeSimple,
  blog: Article,
  docs: Book,
};

export const AdminAssetsPage = () => {
  const navigate = useNavigate();
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [docPreview, setDocPreview] = useState(null);

  useEffect(() => {
    axios
      .get(`${API_URL}/api/admin/assets`, auth())
      .then((r) => setSections(r.data.sections))
      .catch((e) => {
        toast.error(e.response?.status === 403 ? 'Admin only' : 'Could not load assets');
        navigate('/');
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  const openDoc = async (filename) => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/assets/doc/${filename}`, auth());
      if (r.data.error) {
        toast.error(r.data.error);
        return;
      }
      setDocPreview(r.data);
    } catch (e) {
      toast.error('Could not read doc');
    }
  };

  const filter = (items) => {
    if (!query) return items;
    const q = query.toLowerCase();
    return items.filter(
      (it) =>
        it.name.toLowerCase().includes(q) ||
        (it.description || '').toLowerCase().includes(q) ||
        (it.tags || []).some((t) => t.toLowerCase().includes(q)),
    );
  };

  const totalCount = sections.reduce((s, x) => s + x.items.length, 0);

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        Loading asset hub…
      </div>
    );

  return (
    <div className="min-h-screen bg-background" data-testid="admin-assets-page">
      <SEO title="Asset Hub — Admin" description="" noindex={true} />

      <header className="border-b border-border/60 bg-card/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 py-3 gap-4">
          <button
            onClick={() => navigate('/admin')}
            className="flex items-center gap-1.5 text-sm text-muted-foreground"
            data-testid="assets-back-btn"
          >
            <ArrowLeft size={14} /> Back
          </button>
          <div className="flex items-center gap-2 flex-1 max-w-md">
            <MagnifyingGlass size={14} className="text-muted-foreground" />
            <Input
              placeholder={`Search ${totalCount} assets…`}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-8 text-sm"
              data-testid="assets-search-input"
            />
          </div>
          <span className="text-sm font-bold">Asset Hub</span>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-8">
        {sections.map((section) => {
          const Icon = SECTION_ICONS[section.id] || FileText;
          const filtered = filter(section.items);
          if (filtered.length === 0 && query) return null;
          return (
            <section key={section.id} data-testid={`assets-section-${section.id}`}>
              <div className="flex items-baseline justify-between mb-3 border-b border-border/40 pb-2">
                <div className="flex items-center gap-2">
                  <Icon size={18} className="text-emerald-500" />
                  <h2 className="text-lg font-bold">{section.title}</h2>
                  <span className="text-xs text-muted-foreground">({filtered.length})</span>
                </div>
                <span className="text-xs text-muted-foreground italic">{section.subtitle}</span>
              </div>

              {/* Images render as thumbnails, everything else as list rows */}
              {section.id === 'graphics' || section.id === 'merch' || section.id === 'pwa' ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {filtered.map((it) => (
                    <AssetThumb key={it.name} item={it} />
                  ))}
                </div>
              ) : (
                <div className="space-y-2">
                  {filtered.map((it) => (
                    <AssetRow
                      key={`${section.id}-${it.name}`}
                      item={it}
                      onReadDoc={section.id === 'docs' ? () => openDoc(it.name) : null}
                    />
                  ))}
                </div>
              )}
            </section>
          );
        })}

        <p className="text-xs text-muted-foreground italic text-center pt-8 pb-4">
          Every asset we've built lives here. Add something new? It auto-appears on refresh.
        </p>
      </main>

      {/* Doc preview modal */}
      {docPreview && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setDocPreview(null)}
          data-testid="assets-doc-preview"
        >
          <div
            className="bg-background border border-border rounded-lg max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-border/60">
              <h3 className="font-bold font-mono text-sm">{docPreview.name}</h3>
              <button onClick={() => setDocPreview(null)} className="text-muted-foreground hover:text-foreground text-sm">
                Close
              </button>
            </div>
            <pre className="flex-1 overflow-auto p-5 text-xs whitespace-pre-wrap font-mono">
              {docPreview.content}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

const AssetThumb = ({ item }) => {
  const isImage = item.kind === 'image';
  return (
    <a
      href={item.url}
      target="_blank"
      rel="noreferrer"
      className="group block rounded-lg border border-border/50 bg-card hover:border-emerald-500/50 transition-colors overflow-hidden"
      data-testid={`asset-thumb-${item.name}`}
    >
      <div className="aspect-square bg-slate-900 flex items-center justify-center overflow-hidden">
        {isImage ? (
          <img src={item.url} alt={item.name} className="max-w-full max-h-full object-contain" loading="lazy" />
        ) : (
          <FileText size={40} className="text-muted-foreground" />
        )}
      </div>
      <div className="p-2">
        <div className="text-[11px] font-mono truncate" title={item.name}>
          {item.name}
        </div>
        {item.description && <div className="text-[10px] text-muted-foreground line-clamp-2 leading-snug mt-0.5">{item.description}</div>}
        <div className="flex items-center justify-between mt-1">
          <span className="text-[10px] text-muted-foreground">{item.size_human}</span>
          <Download size={11} className="text-muted-foreground group-hover:text-emerald-500" />
        </div>
      </div>
    </a>
  );
};

const AssetRow = ({ item, onReadDoc }) => {
  const handleClick = (e) => {
    if (onReadDoc) {
      e.preventDefault();
      onReadDoc();
    }
  };
  return (
    <div className="flex items-center gap-3 rounded-md border border-border/40 bg-card px-4 py-2.5 hover:border-emerald-500/40 transition-colors">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium truncate" title={item.name}>
            {item.name}
          </span>
          {item.size_human === 'LIVE' && (
            <span className="text-[9px] uppercase tracking-wider text-emerald-400 bg-emerald-400/10 rounded px-1.5 py-0.5">Live</span>
          )}
          {item.size_human === 'DRAFT' && (
            <span className="text-[9px] uppercase tracking-wider text-amber-400 bg-amber-400/10 rounded px-1.5 py-0.5">Draft</span>
          )}
        </div>
        {item.description && (
          <div className="text-xs text-muted-foreground leading-snug mt-0.5 line-clamp-2">{item.description}</div>
        )}
      </div>
      {item.url && !onReadDoc && (
        <Button variant="outline" size="sm" asChild data-testid={`asset-open-${item.name}`}>
          <a href={item.url} target="_blank" rel="noreferrer">
            <Eye size={12} className="mr-1" /> Open
          </a>
        </Button>
      )}
      {onReadDoc && (
        <Button variant="outline" size="sm" onClick={onReadDoc} data-testid={`asset-read-${item.name}`}>
          <Eye size={12} className="mr-1" /> Read
        </Button>
      )}
      {item.extra_url && (
        <Button variant="ghost" size="sm" asChild>
          <a href={item.extra_url} target="_blank" rel="noreferrer">
            <ImageIcon size={12} className="mr-1" /> Preview
          </a>
        </Button>
      )}
    </div>
  );
};

export default AdminAssetsPage;
